import React from "react";
import i18next from "i18next";
import { Box, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { keyframes } from "@emotion/react";
import { BRAND } from "./lightTheme";

// ─── Animations ───────────────────────────────────────────────────────────────
const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1;   transform: scale(1); }
  50%       { opacity: 0.4; transform: scale(0.7); }
`;

const pulseCentered = keyframes`
  0%, 100% { opacity: 1;   transform: translate(-50%, -50%) scale(1); }
  50%       { opacity: 0.4; transform: translate(-50%, -50%) scale(0.7); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles()(() => ({
  // ── Full-page overlay ──
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(246, 249, 243, 0.85)",
    backdropFilter: "blur(6px)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1400,
    animation: `${fadeIn} 0.2s ease`,
  },
  // ── Inline (non-overlay) ──
  inline: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "3rem 1rem",
    flex: 1,
    animation: `${fadeIn} 0.2s ease`,
  },

  // ── Ring spinner ──
  ring: {
    position: "relative",
    width: "56px",
    height: "56px",
    flexShrink: 0,
  },
  ringTrack: {
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    border: `4px solid ${BRAND.tertiary}`,
  },
  ringHead: {
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    border: "4px solid transparent",
    borderTopColor: BRAND.primary,
    borderRightColor: BRAND.secondary,
    animation: `${spin} 0.75s cubic-bezier(0.5, 0, 0.5, 1) infinite`,
  },
  // Inner dot
  ringDot: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    backgroundColor: BRAND.primary,
    animation: `${pulseCentered} 1.5s ease-in-out infinite`,
  },

  // ── Dots variant ──
  dots: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  dot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    backgroundColor: BRAND.primary,
    animation: `${pulse} 1.2s ease-in-out infinite`,
  },

  // ── Label ──
  label: {
    marginTop: "1.25rem",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
    letterSpacing: "0.01em",
  },
}));

// ─── Component ────────────────────────────────────────────────────────────────
/**
 * LightLoader — composant de chargement réutilisable pour le mode light.
 *
 * Props :
 *   overlay  {boolean}  — plein écran avec fond flouté (défaut: false)
 *   variant  {"ring"|"dots"}  — style du spinner (défaut: "ring")
 *   label    {string}   — texte sous le spinner (optionnel)
 *   size     {"sm"|"md"|"lg"}  — taille (défaut: "md")
 */
export default function LightLoader({
  overlay = false,
  variant = "ring",
  label = i18next.t("light.common.loading"),
  size = "md",
}) {
  const { classes: s } = useStyles();

  const ringSize = { sm: 36, md: 56, lg: 72 }[size];
  const dotSize  = { sm: 7,  md: 10, lg: 14 }[size];

  const spinner = variant === "dots" ? (
    <Box className={s.dots}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          className={s.dot}
          style={{
            width: dotSize,
            height: dotSize,
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
    </Box>
  ) : (
    <Box className={s.ring} style={{ width: ringSize, height: ringSize }}>
      <Box className={s.ringTrack} />
      <Box className={s.ringHead} />
      <Box className={s.ringDot} style={{ width: ringSize * 0.18, height: ringSize * 0.18 }} />
    </Box>
  );

  return (
    <Box className={overlay ? s.overlay : s.inline}>
      {spinner}
      {label && (
        <Typography className={s.label}>{label}</Typography>
      )}
    </Box>
  );
}
